import React, { useState } from 'react';
import toast from 'react-hot-toast';
import {
  BrandCampaign,
  BrandCampaignDetail as BrandCampaignDetailData,
  OpenBrandListingBody,
  closeBrandListing,
  openBrandListing,
} from '../../api/payment/brand-deal.api';
import { BRAND_LISTING_LIMITS, listingStatusLabel, priceRangeLabel, slotsLeft, validateListingForm } from '../../utils/brand-deal';
import { useBrandDealLimits } from '../../hooks/use-brand-deal-limits';
import { ICategory } from '../../models';
import { approxCaLabel, fundingShortfall, holdNote, OrderFunding } from '../../utils/individual-order';
import { showFundingShortfall, showOrderErrorToast } from './order-error-toast';
import { useOrdererSanction } from '../brand-sanction/orderer-sanction-context';
import { SANCTION_LOCK_HINT } from '../../utils/brand-sanction';

/**
 * Khung "Đăng lên sàn" + trạng thái sàn + "Đóng sàn" (Task 4, doanh nghiệp) — sống trong chi tiết
 * chiến dịch (`brand-campaign-detail.tsx`). Chỉ hiện form đăng khi chiến dịch `Approved` và chưa đăng
 * sàn (`listingStatus = None`) — đăng rồi thì không đăng lại lần hai (spec §2.2).
 */

const errorMessage = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

const fmtVnd = (n: number) => `${Number(n || 0).toLocaleString('vi-VN')}đ`;

const formatDate = (iso: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

interface BrandCampaignListingPanelProps {
  campaignId: string;
  campaign: BrandCampaign;
  detail: BrandCampaignDetailData;
  /** Nguồn tiền của tài khoản sở hữu chiến dịch: ví doanh nghiệp (VND) hoặc Ca nạp của cá nhân. */
  funding: OrderFunding;
  /** Sau khi giữ hoặc hoàn tiền thì cha tải lại số dư. */
  onFundsChanged?: () => void;
  categories: ICategory[];
  onChanged: (detail: BrandCampaignDetailData) => void;
  /** Đóng sàn tự đóng mọi đơn Pending ở backend trong cùng lần lưu — cha cần refetch tab "Đơn xin
   * tham gia" (nếu đang mở) để không còn hiện nút Duyệt/Từ chối cho đơn đã bị đóng. */
  onClosed: () => void;
  /** "market" (mặc định, M2 — chữ "sàn") | "brief" (M3 Task 7, brief sáng tạo — chữ "brief", cùng 1
   * component/logic đăng/đóng, chỉ đổi chữ hiển thị theo task-7-brief.md). */
  variant?: 'market' | 'brief';
  /** M5 (Task 3): chuyển trang cha sang mục "Chờ chấm điểm" — hiện nút "Chấm điểm ngay" trên toast lỗi
   * "Bạn cần chấm điểm…" khi đăng sàn/brief bị chặn vì còn deal chưa chấm. */
  onGoToRatingsTab?: () => void;
}

const BrandCampaignListingPanel: React.FC<BrandCampaignListingPanelProps> = ({
  campaignId,
  campaign,
  detail,
  funding,
  onFundsChanged,
  categories,
  onChanged,
  onClosed,
  variant = 'market',
  onGoToRatingsTab,
}) => {
  const isBrief = variant === 'brief';
  const noun = isBrief ? 'brief' : 'sàn';
  // Đăng sàn, đăng brief, mở thêm suất bị chặn khi đang bị khoá tạo việc; đóng sàn vẫn được (RB8).
  const { blocked } = useOrdererSanction();
  const limits = useBrandDealLimits();
  // Chiến dịch Công khai đã khai sẵn thông số sàn lúc tạo (vd tự lên sàn thất bại) ⇒ điền lại đúng các số đó.
  const [slotPriceVnd, setSlotPriceVnd] = useState(campaign.slotPriceVnd || 500000);
  const [slotsTotal, setSlotsTotal] = useState(campaign.slotsTotal || 10);
  const [minFollowers, setMinFollowers] = useState(campaign.minFollowers || 0);
  const [categoryIds, setCategoryIds] = useState<string[]>(campaign.categoryIds ?? []);
  const [applyDays, setApplyDays] = useState<number>(campaign.listingApplyDays || BRAND_LISTING_LIMITS.defaultApplyDays);
  const [busy, setBusy] = useState(false);

  const toggleCategory = (id: string) => {
    setCategoryIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const submitOpen = async () => {
    const error = validateListingForm({ slotPriceVnd, slotsTotal, applyDays }, limits.minPriceVnd);
    if (error) {
      toast.error(error);
      return;
    }
    const parts = [{ unitVnd: slotPriceVnd, count: slotsTotal }]; // giữ theo từng suất (R1)
    const shortfall = fundingShortfall(funding, parts, `Số dư ví không đủ để ký quỹ cho ${noun}.`);
    if (shortfall) {
      showFundingShortfall(funding, shortfall);
      return;
    }
    const confirmText = `${holdNote(funding, parts)} để ${isBrief ? 'đăng brief' : 'đăng lên sàn'}. Tiếp tục?`;
    if (!window.confirm(confirmText)) return;
    try {
      setBusy(true);
      const body: OpenBrandListingBody = { slotPriceVnd, slotsTotal, minFollowers, categoryIds, applyDays };
      const updated = await openBrandListing(campaignId, body);
      toast.success(isBrief ? 'Đã đăng brief.' : 'Đã đăng chiến dịch lên sàn.');
      onChanged(updated);
      onFundsChanged?.();
    } catch (e) {
      showOrderErrorToast(e, isBrief ? 'Đăng brief thất bại.' : 'Đăng lên sàn thất bại.', onGoToRatingsTab);
    } finally {
      setBusy(false);
    }
  };

  const submitClose = async () => {
    // M-6 (final review): hiện đúng số tiền thật sẽ hoàn (pool = ListingHeld + ListingSlotReturned −
    // ListingTransferred − ListingRefunded, kẹp ở 0 — final review backend I-1), thay vì câu chung chung
    // "phần chưa dùng".
    const closeConfirm =
      funding.kind === 'Individual'
        ? `Đóng ${noun}? Ca của các suất chưa dùng sẽ được hoàn về Ca nạp.`
        : `Đóng ${noun}? Phần chưa dùng (${fmtVnd(pool)}) sẽ được hoàn về ví.`;
    if (!window.confirm(closeConfirm)) return;
    try {
      setBusy(true);
      const updated = await closeBrandListing(campaignId);
      toast.success(isBrief ? 'Đã đóng brief.' : 'Đã đóng sàn.');
      onChanged(updated);
      onFundsChanged?.();
      onClosed();
    } catch (e) {
      toast.error(errorMessage(e, isBrief ? 'Đóng brief thất bại.' : 'Đóng sàn thất bại.'));
    } finally {
      setBusy(false);
    }
  };

  if (campaign.listingStatus === 'None') {
    if (campaign.status !== 'Approved') return null;
    // Chiến dịch Riêng tư không đăng lên sàn được (backend cũng chặn) ⇒ không hiện form đăng.
    if (campaign.visibility === 'Private') return null;
    return (
      <div className="rounded-xl border ui-divider p-4 space-y-3">
        <div className="font-semibold ui-text">{isBrief ? 'Đăng brief' : 'Đăng lên sàn'}</div>
        <p className="text-xs ui-muted">
          {isBrief
            ? 'Đăng brief để creator khắp nơi nhận nhiệm vụ — brief chỉ đăng được một lần, muốn mở lại thì tạo brief mới.'
            : 'Mở sàn để creator khắp nơi xin tham gia — chiến dịch chỉ đăng lên sàn được một lần, muốn mở lại thì tạo chiến dịch mới.'}
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block text-sm ui-text">
            Giá mỗi suất ({priceRangeLabel(limits.minPriceVnd)})
            <input
              type="number"
              value={slotPriceVnd}
              onChange={(e) => setSlotPriceVnd(Number(e.target.value))}
              className="mt-1 w-full rounded-md border ui-divider bg-transparent px-3 py-2 text-sm outline-none ui-text"
            />
          </label>
          <label className="block text-sm ui-text">
            Số suất (1–100)
            <input
              type="number"
              value={slotsTotal}
              onChange={(e) => setSlotsTotal(Number(e.target.value))}
              className="mt-1 w-full rounded-md border ui-divider bg-transparent px-3 py-2 text-sm outline-none ui-text"
            />
          </label>
          <label className="block text-sm ui-text">
            Người theo dõi tối thiểu (0 = không yêu cầu)
            <input
              type="number"
              min={0}
              value={minFollowers}
              onChange={(e) => setMinFollowers(Math.max(0, Number(e.target.value)))}
              className="mt-1 w-full rounded-md border ui-divider bg-transparent px-3 py-2 text-sm outline-none ui-text"
            />
          </label>
          <label className="block text-sm ui-text">
            Hạn nhận đơn (1–30 ngày)
            <input
              type="number"
              value={applyDays}
              onChange={(e) => setApplyDays(Number(e.target.value))}
              className="mt-1 w-full rounded-md border ui-divider bg-transparent px-3 py-2 text-sm outline-none ui-text"
            />
          </label>
        </div>
        <div>
          <div className="mb-1 text-sm ui-text">Chủ đề (bỏ trống = mọi chủ đề)</div>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => toggleCategory(c.id)}
                className={`rounded-full border px-3 py-1 text-xs font-medium ${
                  categoryIds.includes(c.id)
                    ? 'border-[#0057FF] bg-[#0057FF] text-white'
                    : 'ui-divider ui-text'
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>
        <p className="text-[12px] ui-text">{holdNote(funding, [{ unitVnd: slotPriceVnd, count: slotsTotal }])}.</p>
        {funding.kind === 'Individual' && (
          <p className="text-[12px] font-semibold text-[#0057FF] dark:text-blue-300">
            {approxCaLabel([{ unitVnd: slotPriceVnd, count: slotsTotal }], funding.vndPerCa)} cho {slotsTotal} suất
          </p>
        )}
        {blocked && <p className="text-[12px] text-rose-600 dark:text-rose-300">{SANCTION_LOCK_HINT}</p>}
        <button
          type="button"
          disabled={busy || blocked}
          onClick={submitOpen}
          className="rounded-lg bg-[#0057FF] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
        >
          {busy ? 'Đang đăng…' : isBrief ? 'Đăng brief' : 'Đăng lên sàn'}
        </button>
      </div>
    );
  }

  const total = campaign.slotsTotal || 0;
  const left = slotsLeft(total, campaign.slotsUsed);
  const pct = total > 0 ? Math.min(100, Math.round((campaign.slotsUsed / total) * 100)) : 0;
  // Final review backend I-1: quỹ còn lại = đã giữ + đã trả lại quỹ (vd đơn bị từ chối/đóng sàn) - đã
  // chuyển - đã hoàn, kẹp ở 0 (không hiện số âm dù dữ liệu lịch sử có lệch).
  const pool = Math.max(
    0,
    detail.listingHeldVnd + (detail.listingSlotReturnedVnd ?? 0) - detail.listingTransferredVnd - detail.listingRefundedVnd,
  );

  return (
    <div className="rounded-xl border ui-divider p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="font-semibold ui-text">{isBrief ? 'Brief sáng tạo' : 'Sàn chiến dịch'}</div>
        <span className="ui-divider inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ui-text">
          {listingStatusLabel(campaign.listingStatus)}
        </span>
      </div>

      <div className="space-y-1">
        <div className="flex items-center justify-between text-xs ui-muted">
          <span>Suất đã dùng</span>
          <span>
            {campaign.slotsUsed}/{total} (còn {left})
          </span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
          <div className="h-full bg-[#0057FF]" style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        <div className="rounded-lg border ui-divider p-3">
          <div className="text-xs ui-muted">Giá mỗi suất</div>
          <div className="text-sm font-bold ui-text">{fmtVnd(campaign.slotPriceVnd || 0)}</div>
        </div>
        <div className="rounded-lg border ui-divider p-3">
          <div className="text-xs ui-muted">Tiền còn trong ký quỹ {noun}</div>
          <div className="text-sm font-bold ui-text">{fmtVnd(pool)}</div>
        </div>
        <div className="rounded-lg border ui-divider p-3">
          <div className="text-xs ui-muted">Hạn nhận {isBrief ? 'nhiệm vụ' : 'đơn'}</div>
          <div className="text-sm font-bold ui-text">{formatDate(campaign.applyDeadline)}</div>
        </div>
      </div>

      {campaign.listingStatus === 'Open' ? (
        <button
          type="button"
          disabled={busy}
          onClick={submitClose}
          className="rounded-lg border border-rose-200 px-4 py-2 text-sm font-semibold text-rose-600 disabled:opacity-60 dark:border-rose-900/40"
        >
          {busy ? 'Đang đóng…' : isBrief ? 'Đóng brief' : 'Đóng sàn'}
        </button>
      ) : (
        <p className="text-xs ui-muted">
          {isBrief ? 'Brief đã đóng' : 'Sàn đã đóng'} — phần ký quỹ chưa dùng đã được hoàn lại cho bạn.
        </p>
      )}
    </div>
  );
};

export default BrandCampaignListingPanel;
